import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import { SignupSchema } from "../../schemas/index.js";
import Popup from "../../base-components/Popup";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AuthShell from "../../ui/AuthShell";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function SignUp() {
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);

  const handleSignUp = async (values, actions) => {
    const data = {
      company_name: values.companyName,
      company_email: values.email,
      password: values.password,
      contact: values.contact,
      role: "Admin",
    };

    try {
      const response = await fetch(`${BASE_URL}company/registerCompany`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const responseBody = await response.json();

      if (response.ok) {
        setAlertType("success");
        setMessage(responseBody.message);
        localStorage.setItem("user-info", JSON.stringify(responseBody));
        sessionStorage.setItem("user-info", JSON.stringify(responseBody));
        setIsPopupOpen(true);
        setTimeout(() => setIsPopupOpen(false), 3000);
        navigate(`/dashboard`);
        actions.resetForm();
      } else {
        setAlertType("error");
        setMessage(responseBody.message);
        setIsPopupOpen(true);
        setTimeout(() => setIsPopupOpen(false), 3000);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fieldClass = (hasError) =>
    `h-11 w-full rounded-gx-control border bg-gx-surface px-3 text-gx-ink outline-none focus:ring-2 focus:ring-gx-teal ${
      hasError ? "border-gx-red" : "border-gx-border"
    }`;

  return (
    <AuthShell
      title="Join Now"
      subtitle="Create a company account to start running your club on GymXS."
      footer={
        <p className="text-sm text-gx-ink-soft mt-6">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-gx-red">
            Sign in
          </Link>
        </p>
      }
    >
      <Formik
        initialValues={{
          companyName: "",
          email: "",
          password: "",
          confirmPassword: "",
          contact: "",
        }}
        validationSchema={SignupSchema}
        onSubmit={handleSignUp}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <div className="flex flex-col gap-2 mb-4">
              <label htmlFor="companyName" className="text-sm font-medium text-gx-ink">
                Company name
              </label>
              <Field
                id="companyName"
                name="companyName"
                type="text"
                className={fieldClass(touched.companyName && errors.companyName)}
              />
              {touched.companyName && errors.companyName && (
                <p className="text-sm text-gx-red">{errors.companyName}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 mb-4">
              <label htmlFor="email" className="text-sm font-medium text-gx-ink">
                Email
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                className={fieldClass(touched.email && errors.email)}
              />
              {touched.email && errors.email && (
                <p className="text-sm text-gx-red">{errors.email}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 mb-4">
              <label htmlFor="password" className="text-sm font-medium text-gx-ink">
                Password
              </label>
              <div className="relative">
                <Field
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className={fieldClass(touched.password && errors.password)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-gx-ink-soft"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {touched.password && errors.password && (
                <p className="text-sm text-gx-red">{errors.password}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 mb-4">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-gx-ink">
                Confirm password
              </label>
              <div className="relative">
                <Field
                  id="confirmPassword"
                  name="confirmPassword"
                  type={confirmPassword ? "text" : "password"}
                  className={fieldClass(touched.confirmPassword && errors.confirmPassword)}
                />
                <button
                  type="button"
                  aria-label={confirmPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-gx-ink-soft"
                  onClick={() => setConfirmPassword(!confirmPassword)}
                >
                  <FontAwesomeIcon icon={confirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className="text-sm text-gx-red">{errors.confirmPassword}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 mb-5">
              <label htmlFor="contact" className="text-sm font-medium text-gx-ink">
                Contact number
              </label>
              <Field
                id="contact"
                name="contact"
                type="text"
                className={fieldClass(touched.contact && errors.contact)}
              />
              {touched.contact && errors.contact && (
                <p className="text-sm text-gx-red">{errors.contact}</p>
              )}
            </div>
            <button type="submit" disabled={isSubmitting} className="gx-cta w-full h-11">
              {isSubmitting ? "Creating account" : "Join Now"}
            </button>
          </Form>
        )}
      </Formik>
      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={alertType}
          message={message}
        />
      )}
    </AuthShell>
  );
}

export default SignUp;
